import React, { createContext, useContext, useMemo } from 'react';
import { usePreferences } from '../preferences/PreferencesContext';
import en from './locales/en';
import id from './locales/id';
import type { MessageTree, TranslationParams } from './types';

const catalogs: Record<'id' | 'en', MessageTree> = { id, en };

/**
 * Walks the catalog by dot path, but a segment may itself contain dots.
 *
 * Some entries are keyed by an identifier that already has dots in it — WhatsApp
 * template codes like `templates['booking.confirmed']` — so splitting on every dot
 * would walk past them and return the raw key. Nested lookups are tried first, then
 * progressively longer literal joins of what is left.
 */
function lookup(tree: MessageTree, key: string): string | undefined {
  const parts = key.split('.');

  const walk = (node: string | MessageTree | undefined, from: number): string | undefined => {
    if (node == null) return undefined;
    if (from === parts.length) return typeof node === 'string' ? node : undefined;
    if (typeof node === 'string') return undefined;

    for (let to = from + 1; to <= parts.length; to++) {
      const hit = walk(node[parts.slice(from, to).join('.')], to);
      if (hit !== undefined) return hit;
    }
    return undefined;
  };

  return walk(tree, 0);
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    params[name] != null ? String(params[name]) : `{${name}}`,
  );
}

type I18nContextValue = {
  t: (key: string, params?: TranslationParams) => string;
  locale: 'id' | 'en';
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { locale } = usePreferences();
  const value = useMemo(() => {
    const catalog = catalogs[locale] ?? catalogs.id;
    const fallback = catalogs.en;
    const t = (key: string, params?: TranslationParams) => {
      const raw = lookup(catalog, key) ?? lookup(fallback, key) ?? key;
      return interpolate(raw, params);
    };
    return { t, locale };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>.');
  return ctx;
}
